import * as Sentry from '@sentry/react'
import React, { useState, useEffect, useRef, useCallback } from 'react'
import Header from '@/components/Header'
import Footer from '@/components/Footer'
import TextQuestionCard from '@/components/TextQuestionCard'
import EnvironmentRecording from '@/components/EnvironmentRecording'
import { useInterview } from '@/contexts/InterviewContext'
import { CandidateResponse } from '@/contexts/InterviewContext'
import { useToast } from '@/hooks/use-toast'
import { useDocumentVisibility } from '@/hooks/useDocumentVisibility'
import { useFaceTracking } from '@/hooks/useFaceTracking'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Link, useNavigate } from 'react-router-dom'
import SpeedTest from '@cloudflare/speedtest'
import {
  CheckCircle,
  ArrowRight,
  Video,
  VideoOff,
  Timer,
  AlertCircle,
  KeyRound,
  Check,
  Loader2,
  WifiOff,
} from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import ConsentCheckbox from '@/components/ConsentCheckbox'
import FaceDetectionOverlay from '@/components/FaceDetectionOverlay'
import { TestAssignment, useEmployee } from '@/contexts/EmployeeContext'
import {
  SidebarProvider,
  Sidebar,
  SidebarContent,
  SidebarInset,
  SidebarHeader,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
} from '@/components/ui/sidebar'
import { API_BASE_URL, authedFetch } from '@/utils/apiConfig'
import InformationConsentCheckbox from '@/components/InformationConsentCheckbox'
import { useIsMobile } from '@/hooks/use-mobile'
import { CheatingEvent } from '@/utils/cheatEventUtils'
import { createHybridLiveUploader, HybridLiveUploader } from '@/hybridLiveUploader'
import { supabase } from '@/integrations/supabase/client'
import EnvCheckModal from '@/components/EnvCheckModal'
import { storeSimpleVideoRecord } from '@/utils/videoRecordStatus'
import LipSpeechDetection from '@/components/LipSpeechDetection'
import {
  HEARTBEAT_INTERVAL_MS,
  HEARTBEAT_REQUEST_TIMEOUT_MS,
  canHeartbeatRecoverConnection,
} from '@/utils/heartbeat'

type AnyUploader = HybridLiveUploader

async function setAssignmentStatusSupabase(
  assignmentId: string,
  status:
    | 'pending'
    | 'in_progress'
    | 'completed'
    | 'abandoned'
    | 'expired'
    | 'timeout'
    | 'camera_access_error'
    | 'chunk_upload_error'
    | 'speed_test_fail'
) {
  const payload: any = {
    status,
    updated_at: new Date().toISOString(),
  }

  if (status === 'in_progress') {
    payload.started_at = new Date().toISOString()
    payload.last_heartbeat_at = new Date().toISOString()
    payload.completed_at = null
  }

  if (
    ['timeout', 'abandoned', 'camera_access_error', 'expired', 'speed_test_fail'].includes(status)
  ) {
    payload.completed_at = new Date().toISOString()
  }

  const { data, error } = await supabase
    .from('employee_tests')
    .update(payload)
    .eq('id', assignmentId)
    .select('id,status,started_at,completed_at,updated_at')

  if (error) {
    console.error('Supabase update error:', error, 'assignmentId:', assignmentId)
    throw error
  }

  if (!data || data.length === 0) {
    console.warn('0 rows updated (RLS/filter). Assignment ID:', assignmentId)
  } else {
    Sentry.logger.info(`Assignment status updated successfully: to ${status} for ${assignmentId}`)
  }

  return data
}

async function markInProgressSupabase(assignmentId: string) {
  Sentry.logger.info(`Marking assignment as in_progress for assignmentId: ${assignmentId}`)
  return setAssignmentStatusSupabase(assignmentId, 'in_progress')
}

const s3LogError = async (
  assignmentId: string,
  employeeId: string,
  eventType: string,
  message: string,
  stack?: string
) => {
  try {
    const { error } = await supabase.from('assignment_logs').insert({
      assignment_id: assignmentId,
      employee_id: employeeId,
      event_type: eventType,
      message: message,
      stack: stack || null,
      timestamp: new Date().toISOString(),
    })

    if (error) {
      console.error('Failed to log error to database:', error, 'assignmentId:', assignmentId)
    }
  } catch (err) {
    console.error('Error logging to database:', err, 'assignmentId:', assignmentId)
  }
}

const VideoInterview = () => {
  const {
    questions,
    currentQuestionIndex,
    setCurrentQuestionIndex,
    responses,
    candidateName,
    setCandidateName,
    isInterviewActive,
    nextQuestion,
    previousQuestion,
    addResponse,
    submitInterview,
    startInterview,
    endInterview,
    setQuestions,
    saveInterviewVideo,
    saveResponse,
    initializeResponses,
  } = useInterview()
  const {
    completeAssignment,
    verifyTestAccessCode,
    getAssignmentById,
    getTestById,
    getEmployeeById,
    companyId,
  } = useEmployee()
  const isMobile = useIsMobile()
  const uploaderRef = useRef<AnyUploader | null>(null)

  const navigate = useNavigate()
  const { toast } = useToast()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isCompleted, setIsCompleted] = useState(false)
  const [accessCode, setAccessCode] = useState('')
  const [candidateToken, setCandidateToken] = useState<string | null>(null)
  const [consentChecked, setConsentChecked] = useState(false)
  const [informationConsentChecked, setInformationConsentChecked] = useState(false)
  const [webcamActive, setWebcamActive] = useState(false)
  const [timeLeft, setTimeLeft] = useState(0)
  const [timeLimitSeconds, setTimeLimitSeconds] = useState(0)
  const [cheatingDetected, setCheatingDetected] = useState(false)
  const [faceCount, setFaceCount] = useState(0)
  const [currentTestName, setCurrentTestName] = useState('')
  const [assignment, setAssignment] = useState<TestAssignment>()
  const [cameraError, setCameraError] = useState<string | null>(null)
  const [micError, setMicError] = useState<string | null>(null)
  const [interviewKicked, setInterviewKicked] = useState(false)
  const [timeExpired, setTimeExpired] = useState(false)
  const [lockedQuestionIds, setLockedQuestionIds] = useState<Set<string>>(new Set())
  const timeoutHandledRef = useRef(false)
  const [uploadError, setUploadError] = useState(false)
  const [isFinalizing, setIsFinalizing] = useState(false)
  const [currentUploadId, setCurrentUploadId] = useState<string | null>(null)
  const [finalVideoReady, setFinalVideoReady] = useState(false)

  // New states for environment recording
  const [isEnvironmentRecording, setIsEnvironmentRecording] = useState(false)
  const [environmentRecordingCompleted, setEnvironmentRecordingCompleted] = useState(false)
  const [environmentVideoBlob, setEnvironmentVideoBlob] = useState<Blob | null>(null)
  const [envCheckModalOpen, setEnvCheckModalOpen] = useState(false)
  const envCheckModalOpenRef = useRef(envCheckModalOpen)
  const [envCheckCount, setEnvCheckCount] = useState(0)
  const [pausedTimeLeft, setPausedTimeLeft] = useState<number | null>(null)
  const [envCheckQuestionNumbers, setEnvCheckQuestionNumbers] = useState<number[]>([]) // dynamic env check questions

  // Enhanced cheating event tracking with recording start time
  const [cheatingEvents, setCheatingEvents] = useState<CheatingEvent[]>([])
  const cheatingEventsRef = useRef(cheatingEvents) // Initialize ref with initial state
  const [recordingStartTime, setRecordingStartTime] = useState<Date | null>(null)
  const hasSwitchedTabRef = useRef(false)
  const [showWarningModal, setShowWarningModal] = useState(false)
  const [connectionLost, setConnectionLost] = useState(false)
  const recordingStartRef = useRef<number | null>(null)
  const videoRef = useRef<HTMLVideoElement>()
  const streamRef = useRef<MediaStream | null>(null)
  const intervalRef = useRef<number | null>(null)
  const lastFaceCountUpdateRef = useRef<number>(0)
  const timeLeftRef = useRef(0)
  const mediaRecorderRef = useRef<MediaRecorder>()
  const resultId = useRef<string>('')
  const stoppingRef = useRef(false)
  const finalizeBySubmitRef = useRef(false)
  const preloadedAnswersRef = useRef<
    { questionId: string; questionText: string; choiceId: string }[]
  >([])
  const chunkErrorCountRef = useRef(0)
  const consecutiveErrorCountRef = useRef(0)
  const consecutiveErrorStartRef = useRef<number | null>(null)
  const chunksUploadedCountRef = useRef(0)
  const uploadFailHandledRef = useRef(false)
  const maxTotalErrors = 30
  const maxConsecutiveErrors = 15
  const maxConsecutiveErrorMs = 5 * 60 * 1000 // 5 min — guards against slow-bitrate long windows
  const measuredUploadSpeedRef = useRef<number>(0)
  const cameraCheckFailCountRef = useRef(0)
  const micCheckFailCountRef = useRef(0)
  const MEDIA_GRACE_CHECKS = 4 // 4 × 2.5s = 10s grace period before kick

  // Speed test states
  type SpeedTestState = 'idle' | 'running' | 'completed' | 'failed'
  const [speedTestState, setSpeedTestState] = useState<SpeedTestState>('idle')

  const { isVisible, visibilityChangeCount } = useDocumentVisibility()
  const THROTTLE_MS = 500
  const throttledSetFaceCount = useCallback((numFace: number) => {
    const now = Date.now()
    if (now - lastFaceCountUpdateRef.current > THROTTLE_MS) {
      setFaceCount(numFace)
      lastFaceCountUpdateRef.current = now
    }
  }, [])

  useEffect(() => {
    cheatingEventsRef.current = cheatingEvents
  }, [cheatingEvents])

  const handleOpenModal = () => {
    envCheckModalOpenRef.current = true // Sync update first
    setEnvCheckModalOpen(true)
  }

  const handleCloseModal = () => {
    envCheckModalOpenRef.current = false // Sync update first
    setEnvCheckModalOpen(false)
  }

  useEffect(() => {
    envCheckModalOpenRef.current = envCheckModalOpen
  }, [envCheckModalOpen])

  useEffect(() => {
    timeLeftRef.current = timeLeft
  }, [timeLeft])

  useEffect(() => {
    if (assignment?.id) {
      Sentry.setTag('assignmentId', assignment.id)
    }
  }, [assignment?.id])

  // Heartbeat is diagnostic only: it persists elapsed time and last_heartbeat_at every
  // 30 seconds, but a missed request must not interrupt the candidate's exam.
  useEffect(() => {
    if (!isInterviewActive || !assignment?.id || !candidateToken) return

    let active = true

    const sendHeartbeat = async () => {
      const elapsed = Math.max(0, timeLimitSeconds - timeLeftRef.current)
      try {
        const response = await fetch(`${API_BASE_URL}/api/assignments/update_time`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            assignmentId: assignment.id,
            timeSpentSeconds: elapsed,
            candidateToken,
          }),
          signal: AbortSignal.timeout(HEARTBEAT_REQUEST_TIMEOUT_MS),
        })
        if (!response.ok) throw new Error(`Heartbeat failed with ${response.status}`)
        const body = await response.json().catch(() => null)
        if (!active) return
        // Preserve answer-save recovery only while the server still accepts answers.
        if (canHeartbeatRecoverConnection(body?.status)) setConnectionLost(false)
      } catch {
        // Passive diagnostic signal: keep the exam running and try again next interval.
      }
    }

    void sendHeartbeat()
    const saveTimer = setInterval(() => void sendHeartbeat(), HEARTBEAT_INTERVAL_MS)

    return () => {
      active = false
      clearInterval(saveTimer)
      setConnectionLost(false)
    }
  }, [isInterviewActive, assignment?.id, candidateToken, timeLimitSeconds])

  // Enhanced function to add cheating events with offset calculation
  const addCheatingEvent = useCallback(
    (type: CheatingEvent['type'], metadata?: CheatingEvent['metadata']) => {
      // Suppress cheat detection while environment-check modal is open, but keep the video recording running.
      if (envCheckModalOpenRef.current) {
        return
      }

      const timestamp = new Date()
      const now = Date.now()
      const start = recordingStartRef.current
      const offset = start != null ? (now - start) / 1000 : 0
      const newEvent: CheatingEvent = {
        type,
        timestamp: timestamp.toISOString(),
        offset,
        metadata,
      }

      // Send to backend with the correct structure for Python
      sendCheatingEvents(assignment.id, {
        ...newEvent,
        // Add event_data field for backend compatibility
        metadata: metadata,
      })

      setCheatingEvents((prev) => [...prev, newEvent])
      if (!cheatingDetected) {
        setCheatingDetected(true)
      }
    },
    [cheatingDetected, assignment, envCheckModalOpen, companyId]
  )
  // Function to send cheating events to backend
  const sendCheatingEvents = async (assignmentId: string, event: CheatingEvent) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/assignments/cheating_events`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          assignmentId,
          cheatingEvents: [event],
          companyId: companyId,
          attemptNumber: assignment?.attempt_count || 1,
        }),
      })
      if (response.ok) {
      } else {
        console.error(
          'Failed to send cheating events:',
          response.statusText,
          'assignmentId:',
          assignmentId
        )
      }
    } catch (error) {
      console.error('Error sending cheating events:', error, 'assignmentId:', assignmentId)
    }
  }
  const { faceDetected, multipleFaces, isLookingAway, handleGazeUpdate, updateFaceDetection } =
    useFaceTracking(videoRef, webcamActive, {
      onFaceDetectionChange: (detected) => {
        if (!detected) {
          addCheatingEvent('NO_FACE_DETECTED')
        }
      },
      onMultipleFacesChange: (multiple, numFace) => {
        if (multiple) {
          addCheatingEvent('MULTIPLE_FACES_DETECTED', {
            faceCount: numFace,
          })
        }
        throttledSetFaceCount(numFace)
      },
      onGazeChange: (lookingAway) => {
        if (lookingAway) {
          addCheatingEvent('LOOKING_AWAY')
        }
      },
      onEvent: (event) => {
        addCheatingEvent(event.type as CheatingEvent['type'])
      },
    })
  const currentQuestion = questions[currentQuestionIndex]
  const currentResponse = responses.find((r) => r.questionId === currentQuestion?.id)
  const hasResponse = !!currentResponse
  const isLastQuestion = currentQuestionIndex === questions.length - 1
  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  const triggerChunkUploadFail = async (reason?: string, originalError?: Error) => {
    if (uploadFailHandledRef.current) return
    uploadFailHandledRef.current = true

    Sentry.logger.info(
      `[VideoExam] event=kick | assignmentId=${assignment.id} | reason=chunk_upload_error | details=${reason ?? 'unknown'} | totalErrors=${chunkErrorCountRef.current} | consecutiveErrors=${consecutiveErrorCountRef.current} | chunksUploaded=${chunksUploadedCountRef.current}`
    )
    Sentry.logger.info(`[Upload Fail] Triggered: ${reason}, assignmentId: ${assignment.id}`)

    Sentry.captureException(originalError ?? new Error('Upload failed'), {
      tags: { event: 'upload_kick' },
      extra: {
        reason,
        totalErrors: chunkErrorCountRef.current,
        consecutiveErrors: consecutiveErrorCountRef.current,
        chunksUploaded: chunksUploadedCountRef.current,
      },
    })

    await s3LogError(
      assignment.id,
      assignment.employee_id,
      'chunk_upload_error',
      `Video upload failed: ${reason}`,
      new Error().stack
    )

    try {
      // Stop recording immediately
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop()
      }
    } catch (err) {
      console.error('[Upload Fail] Error stopping recorder:', err, 'assignmentId:', assignment.id)
      await s3LogError(
        assignment.id,
        assignment.employee_id,
        'recorder_stop_error',
        `Error stopping recorder: ${err instanceof Error ? err.message : String(err)}`,
        err instanceof Error ? err.stack : undefined
      )
    }

    setUploadError(true)
    setIsCompleted(true)

    // Set assignment status
    if (assignment?.id) {
      Sentry.logger.info(
        `Setting assignment status to chunk_upload_error for assignmentId: ${assignment.id}`
      )
      setAssignmentStatusSupabase(assignment.id, 'chunk_upload_error').catch((err) => {
        console.error(
          '[Upload Fail] Failed to set assignment status:',
          err,
          'assignmentId:',
          assignment.id
        )
      })
    }

    toast({
      title: 'Video Yükleme Hatası',
      description:
        'Videolar yüklenemediği için sınav sonlandırıldı. Lütfen internet bağlantınızı kontrol ediniz.',
      variant: 'destructive',
    })

    try {
      uploaderRef.current?.abort(new Error(`Chunk upload failed: ${reason}`))
    } catch (err) {
      console.error('[Upload Fail] Error aborting uploader:', err, 'assignmentId:', assignment.id)
    }
  }

  const startWebcam = async () => {
    try {
      setWebcamActive(true)
      setUploadError(false)
      uploadFailHandledRef.current = false

      // Reset error counters
      chunkErrorCountRef.current = 0
      consecutiveErrorCountRef.current = 0
      consecutiveErrorStartRef.current = null
      chunksUploadedCountRef.current = 0

      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        return
      }

      if (streamRef.current) {
        try {
          streamRef.current.getTracks().forEach((t) => t.stop())
        } catch {}
      }
      stoppingRef.current = false

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: 'user' },
        audio: true,
      })
      streamRef.current = stream

      if (!videoRef.current) {
        console.error('[Webcam] videoRef is null, assignmentId:', assignment.id)
        return
      }
      videoRef.current.srcObject = stream

      const bucket = 'video-recordings'
      const attemptN = assignment.attempt_count || 1
      const finalKey = `${assignment.test_id}/${assignment.id}/attempt_${attemptN}/final_interview.webm`

      uploaderRef.current = createHybridLiveUploader(bucket, finalKey, 'video/webm', {
        partSize: 5 * 1024 * 1024, // 5MB chunks
        queueSize: 2,
        maxRetries: 3,
        retryDelay: 1000,
        firstChunkTimeout: 60000, // 1 minute timeout for first chunk
        saveChunks: true, // Enable individual chunk storage
        employeeTestId: assignment.id, // For chunk tracking
        candidateToken: candidateToken ?? undefined,
        companyId: assignment.company_id, // For chunk tracking
        attemptNumber: attemptN,
        saveUploadId: async (uploadId: string) => {
          setCurrentUploadId(uploadId)
          localStorage.setItem(`upload_${assignment.id}_attempt_${attemptN}`, uploadId)
          Sentry.logger.info(
            `[Webcam] Successfully saved upload_id: ${uploadId} assignmentId: ${assignment.id} attempt: ${attemptN}`
          )
        },
        loadUploadId: async () => {
          const stored = localStorage.getItem(`upload_${assignment.id}_attempt_${attemptN}`)
          if (stored) {
            setCurrentUploadId(stored)
            return stored
          }
          return null
        },
        clearUploadId: async () => {
          setCurrentUploadId(null)
          localStorage.removeItem(`upload_${assignment.id}_attempt_${attemptN}`)
          Sentry.logger.info(
            `[Webcam] Cleared upload_id for assignmentId: ${assignment.id} attempt: ${attemptN}`
          )
        },
        completeUploadEndpoint: `${API_BASE_URL}/api/complete-upload`,
        onEvent: (event) => {
          Sentry.logger.info(
            `[Hybrid Uploader Event] type=${event.type} assignmentId: ${assignment.id}`
          )

          if (event.type === 'first-chunk-error') {
            // Trigger upload fail immediately for first chunk failure
            const originalError =
              event.error instanceof Error
                ? event.error
                : new Error(
                    (event.error as any)?.message ||
                      String(event.error) ||
                      'First chunk upload failed'
                  )
            void triggerChunkUploadFail('First chunk upload failed', originalError)
            return
          } else if (event.type === 'retry') {
            Sentry.logger.info(
              `[VideoExam] event=retry | assignmentId=${assignment.id} | chunkNumber=${event.chunkNumber ?? 0} | attempt=${event.attempt ?? 0}`
            )
          } else if (event.type === 'error') {
            chunkErrorCountRef.current += 1
            consecutiveErrorCountRef.current += 1
            if (consecutiveErrorStartRef.current === null) {
              consecutiveErrorStartRef.current = Date.now()
            }

            Sentry.logger.info(
              `[VideoExam] event=chunk_counts | assignmentId=${assignment.id} | totalErrors=${chunkErrorCountRef.current} | consecutiveErrors=${consecutiveErrorCountRef.current} | chunksUploaded=${chunksUploadedCountRef.current}`
            )

            const consecutiveDurationMs =
              Date.now() - (consecutiveErrorStartRef.current ?? Date.now())
            // Check termination conditions
            if (
              chunkErrorCountRef.current >= maxTotalErrors ||
              consecutiveErrorCountRef.current >= maxConsecutiveErrors ||
              consecutiveDurationMs >= maxConsecutiveErrorMs
            ) {
              const errorMessage =
                (event.error as any)?.message || String(event.error) || 'Unknown error'
              Sentry.logger.info(
                `[Webcam] Upload failed: ${errorMessage} assignmentId: ${assignment.id}`
              )
              const originalError =
                event.error instanceof Error ? event.error : new Error(errorMessage)
              void triggerChunkUploadFail('Too many errors', originalError)
              return
            }
          } else if (event.type === 'chunk-uploaded') {
            chunksUploadedCountRef.current += 1
            // Reset consecutive errors on successful chunk upload to final video
            consecutiveErrorCountRef.current = 0
            consecutiveErrorStartRef.current = null
            Sentry.logger.info(
              `[Upload Success] Part ${event.chunkInfo?.chunkNumber} uploaded to final video assignmentId: ${assignment.id}`
            )
          } else if (event.type === 'chunk-saved') {
            // Individual chunk backup completed
            Sentry.logger.info(
              `[Chunk Backup] Saved individual chunk: ${event.chunkInfo?.chunkKey}, assignmentId: ${assignment.id}`
            )
          } else if (event.type === 'progress') {
            consecutiveErrorCountRef.current = 0
            consecutiveErrorStartRef.current = null
            Sentry.logger.info(
              `[Upload Progress] ${event.progress?.loaded} bytes uploaded, assignmentId: ${assignment.id}`
            )
          } else if (event.type === 'upload-id-available') {
            setCurrentUploadId(event.uploadId!)
          } else if (event.type === 'state') {
            Sentry.logger.info(`[Upload State] ${event.state}, assignmentId: ${assignment.id}`)
            Sentry.logger.info(
              `[Webcam] Upload state changed: ${event.state} assignmentId: ${assignment.id}`
            )
          }
        },
      })

      const pickMime = (): string | undefined => {
        const c = ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm']
        return c.find((m) => (window as any).MediaRecorder?.isTypeSupported?.(m)) || undefined
      }
      const mt = pickMime()

      // Select bitrate based on measured upload speed to reduce kick rate on slow connections
      const getAdaptiveBitrate = (): number => {
        const speed = measuredUploadSpeedRef.current
        if (speed < 1.5) return 200_000 // 200 kbps for slow connections (0.5–1.5 Mbps)
        if (speed < 3) return 350_000 // 350 kbps for medium connections
        return 500_000 // 500 kbps for fast connections
      }

      // Keep a buffer and size counter
      let chunkBuffer: Blob[] = []
      let bufferedSize = 0
      const MAX_CHUNK_SIZE = 5 * 1024 * 1024 // 5 MB — S3 multipart minimum for non-final parts

      const recorder = new MediaRecorder(stream, {
        mimeType: 'video/webm',
        videoBitsPerSecond: getAdaptiveBitrate(),
      })
      mediaRecorderRef.current = recorder

      recorder.ondataavailable = async (e: BlobEvent) => {
        if (!e.data || e.data.size === 0) return

        chunkBuffer.push(e.data)
        bufferedSize += e.data.size

        if (bufferedSize >= MAX_CHUNK_SIZE && !stoppingRef.current) {
          const combined = new Blob(chunkBuffer, { type: e.data.type })
          chunkBuffer = []
          bufferedSize = 0

          try {
            await uploaderRef.current?.enqueue(combined)
            Sentry.logger.info(
              `[Webcam] Uploaded chunk: ${combined.size} bytes, assignmentId: ${assignment.id}`
            )
          } catch (err) {
            console.error('[Webcam] Enqueue error:', err, 'assignmentId:', assignment.id)
          }
        }
      }

      recorder.onstop = async () => {
        Sentry.logger.info(`[Webcam] Recording stopped, assignmentId: ${assignment.id}`)
        stoppingRef.current = true

        // 1) Flush any remaining buffered data
        try {
          if (chunkBuffer.length > 0) {
            const combined = new Blob(chunkBuffer, { type: chunkBuffer[0].type })
            chunkBuffer = []
            bufferedSize = 0

            try {
              await uploaderRef.current?.enqueue(combined)
              Sentry.logger.info(
                `[Webcam] Uploaded final chunk: ${combined.size} bytes, assignmentId: ${assignment.id}`
              )
            } catch (err) {
              console.error('[Webcam] Final enqueue error:', err, 'assignmentId:', assignment.id)
            }
          }
        } catch (err) {
          console.error(
            '[Webcam] Error while flushing final chunk:',
            err,
            'assignmentId:',
            assignment.id
          )
        }

        // 2) Stop media tracks
        try {
          stream.getTracks().forEach((t) => t.stop())
        } catch (err) {
          console.error('[Webcam] Error stopping tracks:', err, 'assignmentId:', assignment.id)
        }

        // 3) Update UI state
        setWebcamActive(false)

        // 4) Finalize upload if needed
        if (!finalizeBySubmitRef.current) {
          backgroundFinalize()
        }
      }

      recorder.onerror = (ev) => {
        console.error('[Webcam] recorder error', ev, 'assignmentId:', assignment.id)
        stoppingRef.current = true
        try {
          uploaderRef.current?.abort(ev)
        } catch {}
        try {
          recorder.stop()
        } catch {}
      }

      videoRef.current.onloadedmetadata = () => {
        videoRef.current
          ?.play()
          .then(() => {
            if (isInterviewActive) {
              // Use 2-second chunks for more frequent uploads
              recorder.start(2_000)
              setRecordingStartTime(new Date())
              recordingStartRef.current = Date.now()
            }
          })
          .catch((e) => console.error('[Webcam] play() error', e, 'assignmentId:', assignment.id))
      }
    } catch (err) {
      console.error('[Webcam] Error in startWebcam:', err, 'assignmentId:', assignment.id)
      toast({
        title: 'Kamera Hatası',
        description: 'Kamera başlatılırken bir hata oluştu.',
        variant: 'destructive',
      })
      setWebcamActive(false)
    }
  }

  const stopWebcam = () => {
    try {
      if (mediaRecorderRef.current?.state === 'recording') {
        stoppingRef.current = true
        mediaRecorderRef.current.requestData()
        mediaRecorderRef.current.stop()
      }
    } catch (err) {
      console.error('[Webcam] Error stopping recorder:', err, 'assignmentId:', assignment.id)
    }

    // If recorder was already inactive, then we can cleanup directly:
    if (mediaRecorderRef.current?.state !== 'recording') {
      try {
        streamRef.current?.getTracks().forEach((t) => t.stop())
      } catch {}
      streamRef.current = null
      if (videoRef.current) videoRef.current.srcObject = null
      setWebcamActive(false)
    }
  }

  // Background finalization function (non-blocking)
  const backgroundFinalize = async () => {
    Sentry.logger.info(`[Finalize] attempt: ${assignment?.attempt_count} id: ${assignment?.id}`)
    Sentry.logger.info(`[Finalize] uploadId: ${uploaderRef.current?.getUploadId()}`)
    Sentry.logger.info(
      `[Finalize] uploadedParts: ${uploaderRef.current?.getUploadedParts()?.length ?? 0}`
    )
    Sentry.logger.info(`[Background] Starting finalization for assignmentId: ${assignment.id}`)

    // Check if uploader exists
    if (!uploaderRef.current) {
      console.warn(
        '[Background] No uploader available for finalization, assignmentId:',
        assignment.id
      )

      await s3LogError(
        assignment.id,
        assignment.employee_id,
        'finalization_error',
        'No uploader available for finalization',
        new Error().stack
      )
      return
    }

    // Don't show finalizing UI for background operations
    if (!finalizeBySubmitRef.current) {
      Sentry.logger.info('[Background] Background finalization - not showing UI')
    }

    try {
      const finalKey = await uploaderRef.current?.done()
      Sentry.logger.info(
        `[Background] Upload completed: ${finalKey} assignmentId: ${assignment.id}`
      )

      if (finalKey) {
        Sentry.logger.info(`[Background] Video finalization completed for key: ${finalKey}`)

        // Send to backend
        await storeSimpleVideoRecord(assignment.id, finalKey, 'final', companyId, {
          attempt_number: assignment.attempt_count ?? 1,
          candidateToken: candidateToken ?? undefined,
        })

        setFinalVideoReady(true)
        Sentry.logger.info(
          `[Background] Video finalization completed for assignmentId: ${assignment.id}`
        )
      }
    } catch (err) {
      console.error('[Background] Client finalization failed:', err, 'assignmentId:', assignment.id)

      Sentry.captureException(err instanceof Error ? err : new Error(String(err)), {
        tags: { event: 'finalization_error' },
      })

      await s3LogError(
        assignment.id,
        assignment.employee_id,
        'video_finalization_error',
        `Video finalization failed: ${err instanceof Error ? err.message : String(err)}`,
        err instanceof Error ? err.stack : undefined
      )

      // Try backend completion
      try {
        // Get uploadId from uploader if currentUploadId is null
        const uploadId = currentUploadId || uploaderRef.current?.getUploadId()
        const uploadedParts = uploaderRef.current?.getUploadedParts() || []

        if (!uploadId) {
          console.error(
            '[Background] No upload ID available for backend completion for assignmentId:',
            assignment.id
          )
          throw new Error('No upload ID available for backend completion')
        }

        if (uploadedParts.length === 0) {
          console.error(
            '[Background] No uploaded parts available for backend completion for assignmentId:',
            assignment.id
          )
          throw new Error('No uploaded parts available for backend completion')
        }

        const response = await fetch(`${API_BASE_URL}/api/complete-upload`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            bucket: 'video-recordings',
            key: `${assignment.test_id}/${assignment.id}/attempt_${assignment.attempt_count || 1}/final_interview.webm`,
            uploadId: uploadId,
            companyId: companyId,
            employeeTestId: assignment.id,
            assignmentId: assignment.id,
            candidateToken,
            chunkInfo: uploadedParts.map((part) => ({
              partNumber: part.partNumber,
              chunkKey: part.chunkKey,
              size: part.size,
              etag: part.etag,
            })),
          }),
        })

        const result = await response.json()

        if (response.ok && result.status === 'completed') {
          Sentry.logger.info(
            `[Background] Backend completion successful for key: ${result.key} assignmentId: ${assignment.id}`
          )

          await storeSimpleVideoRecord(assignment.id, result.key, 'final', companyId, {
            candidateToken: candidateToken ?? undefined,
          })
          setFinalVideoReady(true)
          Sentry.logger.info(
            `[Background] Backend completion successful for assignmentId: ${assignment.id}`
          )
        } else {
          console.warn(
            '[Background] Backend completion failed:',
            result,
            'assignmentId:',
            assignment.id
          )
          Sentry.logger.info(
            `[Background] Backend completion failed, manual recovery needed for assignmentId: ${assignment.id}`
          )
        }
      } catch (backendError) {
        console.error(
          '[Background] Backend completion error:',
          backendError,
          'assignmentId:',
          assignment.id
        )

        Sentry.logger.info(
          `[Background] Backend completion error, manual recovery needed for assignmentId: ${assignment.id}`
        )
      }
    }
    console.groupEnd()
  }

  const handleTimeoutAsync = useCallback(async () => {
    if (timeoutHandledRef.current) return
    timeoutHandledRef.current = true

    try {
      // Add null check for assignment
      if (assignment?.id) {
        Sentry.logger.info(
          `[VideoExam] event=kick | assignmentId=${assignment.id} | reason=timeout`
        )
        Sentry.captureException(new Error('Exam timed out'), {
          tags: { event: 'timeout_kick' },
        })
        await setAssignmentStatusSupabase(assignment.id, 'timeout')
      } else {
        console.warn('No assignment ID available for timeout status update')
      }
    } catch (e) {
      console.error('Failed to set timeout status:', e, 'assignmentId:', assignment.id)
    } finally {
      setTimeExpired(true)
      mediaRecorderRef.current?.stop()
      stopWebcam()
      endInterview()
    }
  }, [assignment?.id, endInterview])

  const handleSubmit = async () => {
    if (isCompleted || isSubmitting || connectionLost || !candidateToken) return

    setIsSubmitting(true)
    finalizeBySubmitRef.current = true

    try {
      // Submit interview results first (critical data)
      const allResponses = questions.map((question) => {
        const existingResponse = responses.find((r) => r.questionId === question.id)
        return (
          existingResponse || {
            questionId: question.id,
            questionText: question.text,
            choiceId: '',
          }
        )
      })

      const interviewResultData = {
        candidateId: assignment.employee_id,
        assignmentId: assignment.id,
        testId: assignment.test_id,
        responses: allResponses,
        candidateToken,
      }

      resultId.current = await submitInterview(interviewResultData)
      Sentry.logger.info(`[Submit] Interview results submitted for assignmentId: ${assignment.id}`)

      // Stop recording
      try {
        if (mediaRecorderRef.current?.state === 'recording') {
          mediaRecorderRef.current.requestData()
          stoppingRef.current = true
          mediaRecorderRef.current.stop()
        }
      } catch (err) {
        console.error('[Submit] Error stopping recorder:', err, 'assignmentId:', assignment.id)
      }

      // Clear timer
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }

      // Check if we need to wait for video finalization
      const hasActiveUploads = uploaderRef.current?.hasActiveUploads() || false
      const hasPendingChunks = uploaderRef.current?.getQueueSize() > 0 || false

      if (hasActiveUploads || hasPendingChunks) {
        setIsFinalizing(true)

        // Start finalization with timeout
        const finalizationTimeout = setTimeout(() => {
          Sentry.logger.info(
            `[Submit] Finalization timeout, completing anyway for assignmentId: ${assignment.id}`
          )
          setIsFinalizing(false)
          setIsCompleted(true)
        }, 120000) // 2 minute timeout

        try {
          await backgroundFinalize()
          clearTimeout(finalizationTimeout)
          setIsFinalizing(false)
          setIsCompleted(true)
        } catch (err) {
          console.error('[Submit] Finalization error:', err, 'assignmentId:', assignment.id)
          clearTimeout(finalizationTimeout)
          setIsFinalizing(false)
          setIsCompleted(true)
        }
      } else {
        // No video uploads pending, complete immediately
        Sentry.logger.info(
          `[Submit] No pending uploads, completing immediately for assignmentId: ${assignment.id}`
        )
        await backgroundFinalize()
        setIsCompleted(true)
      }

      // Send completion email (non-blocking)
      const employeeData = await getEmployeeById(assignment.employee_id)
      if (employeeData?.mail_adresi) {
        fetch(`${API_BASE_URL}/api/assignments/send_completion_email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: employeeData.mail_adresi,
            name: candidateName || 'Katılımcı',
            test_name: currentTestName || 'Sınav',
            finish_date: new Date().toISOString(),
          }),
        }).catch((err) =>
          console.error('Error sending completion email:', err, 'assignmentId:', assignment.id)
        )
      }
    } catch (error) {
      console.error('[Submit] Error during submission:', error, 'assignmentId:', assignment.id)

      Sentry.captureException(error instanceof Error ? error : new Error(String(error)), {
        tags: { event: 'submission_error' },
      })

      await s3LogError(
        assignment.id,
        assignment.employee_id,
        'submission_error',
        `Error during interview submission: ${error instanceof Error ? error.message : String(error)}`,
        error instanceof Error ? error.stack : undefined
      )

      toast({
        title: 'Hata',
        description: 'Sınav sonuçları gönderilirken bir hata oluştu.',
        variant: 'destructive',
      })
    } finally {
      setIsSubmitting(false)
      finalizeBySubmitRef.current = false
    }
  }

  const recordSpeedTestResult = async (
    assignmentId: string,
    companyId: string,
    testDataSize: number,
    testDuration: number,
    uploadSpeed: number,
    userAgent: string,
    error: string | null = null
  ) => {
    try {
      const { error: speedTestError } = await supabase.from('speed_test_results').insert({
        assignment_id: assignmentId,
        company_id: companyId,
        test_data_size: testDataSize,
        test_duration: testDuration,
        upload_speed: uploadSpeed,
        user_agent: userAgent,
        error: error,
      })
    } catch (dbError) {
      console.error('[Speed Test] Database error:', dbError, 'assignmentId:', assignmentId)
    }
  }

  const runSpeedTest = async (
    assignmentData: TestAssignment
  ): Promise<{ uploadSpeed: number | null }> => {
    const startTime = Date.now()

    return new Promise(async (resolve) => {
      try {
        const customMeasurements = [
          { type: 'latency', numPackets: 1 },
          { type: 'download', bytes: 1e5, count: 1, bypassMinDuration: true },
          { type: 'upload', bytes: 1e5, count: 2 },
          { type: 'upload', bytes: 1e6, count: 3 },
          { type: 'upload', bytes: 1e7, count: 2 },
        ]

        const test = new SpeedTest({
          autoStart: true,
          measurements: customMeasurements,
        })

        test.onError = async (err) => {
          console.error('[Speed Test] Cloudflare error:', err, 'assignmentId:', assignmentData.id)
          const errorMessage = typeof err === 'string' ? err : 'Unknown SpeedTest Error'

          await recordSpeedTestResult(
            assignmentData.id,
            assignmentData.company_id,
            0,
            (Date.now() - startTime) / 1000,
            0,
            navigator.userAgent,
            errorMessage
          )
          resolve({ uploadSpeed: null })
        }

        test.onFinish = async (results) => {
          const summary = results.getSummary()

          const uploadSpeedMbps = summary.upload / 1000000
          const duration = (Date.now() - startTime) / 1000

          await recordSpeedTestResult(
            assignmentData.id,
            assignmentData.company_id,
            0,
            duration,
            uploadSpeedMbps,
            navigator.userAgent,
            null
          )

          resolve({
            uploadSpeed: uploadSpeedMbps,
          })
        }
      } catch (error) {
        console.error(
          '[Speed Test] Initialization Error:',
          error,
          'assignmentId:',
          assignmentData.id
        )
        resolve({ uploadSpeed: null })
      }
    })
  }

  const handleStartInterview = async (e: React.FormEvent) => {
    e.preventDefault()
    setCandidateToken(null)
    if (!accessCode.trim()) {
      toast({
        title: 'Erişim Kodu Gerekli',
        description: 'Lütfen size verilen erişim kodunu girin.',
        variant: 'destructive',
      })
      return
    }
    if (!consentChecked) {
      toast({
        title: 'Onay Gerekli',
        description: 'Devam etmek için kişisel veri işleme onayını vermeniz gerekiyor.',
        variant: 'destructive',
      })
      return
    }
    if (!informationConsentChecked) {
      toast({
        title: 'Bilgilendirme Onayı Gerekli',
        description:
          'Devam etmek için bilgilendirme videosunu izlemeniz ve sınav kurallarını okumanız gerekiyor.',
        variant: 'destructive',
      })
      return
    }

    try {
      const result = await verifyTestAccessCode(accessCode.trim())
      Sentry.logger.info(
        `Access code verification result: valid=${result.valid} assignmentId=${result.assignmentId ?? 'none'}`
      )
      if (!result.valid) {
        toast({
          title: 'Geçersiz Erişim Kodu',
          description: 'Girdiğiniz erişim kodu geçerli değil. Lütfen kontrol edip tekrar deneyin.',
          variant: 'destructive',
        })
        return
      }
      if (!result.candidateToken) throw new Error('Candidate session token was not returned')
      setCandidateToken(result.candidateToken)

      const assignmentData = result.assignmentId
        ? await getAssignmentById(result.assignmentId)
        : undefined

      // Set assignment immediately after fetching
      setAssignment(assignmentData)

      Sentry.logger.info(`Assignment fetched: ${assignmentData?.id}`)

      if (assignmentData) {
        const now = new Date()
        const assignmentStart = new Date(assignmentData.assignment_start_date)
        if (now < assignmentStart) {
          toast({
            title: 'Sınav Başlatılamaz',
            description: `Sınav ${assignmentStart.toLocaleString('tr-TR')} tarihinden önce başlatılamaz.`,
            variant: 'destructive',
          })
          return
        }

        const employeeData = await getEmployeeById(assignmentData.employee_id)
        setCandidateName(employeeData && employeeData.name ? employeeData.name : 'Misafir')

        const testData = await getTestById(assignmentData.id)
        setCurrentTestName(testData.name || 'Mavi Test')

        if (testData) {
          const limitSec = testData.time_limit ?? 1800
          setTimeLimitSeconds(limitSec)
          setTimeLeft(Math.max(0, limitSec - (assignmentData.time_spent_seconds || 0)))
          const testQuestions = generateQuestionsForTest(testData)
          setQuestions(testQuestions)

          // Store previously saved answers so they can be restored after startInterview() clears state
          if (testData.existing_answers && testData.existing_answers.length > 0) {
            preloadedAnswersRef.current = testData.existing_answers.map(
              (a: { question_id: string; choice_id: string }) => ({
                questionId: a.question_id,
                questionText: '',
                choiceId: a.choice_id ?? '',
              })
            )
            setLockedQuestionIds(
              new Set(testData.existing_answers.map((a: { question_id: string }) => a.question_id))
            )
          } else {
            preloadedAnswersRef.current = []
            setLockedQuestionIds(new Set())
          }

          const totalQuestions = testQuestions.length
          const envCheckQuestions = generateEnvCheckQuestions(totalQuestions)
          setEnvCheckQuestionNumbers(envCheckQuestions)

          try {
            // Run speed test but don't auto-start environment recording
            setSpeedTestState('running')
            const speedResult = await runSpeedTest(assignmentData)
            setSpeedTestState('completed')

            // Store measured speed for adaptive bitrate selection in startWebcam
            measuredUploadSpeedRef.current = speedResult.uploadSpeed ?? 0

            // Check minimum speed requirement; null means the test errored — treat as failure
            const minimumSpeed = 0.5
            if (speedResult.uploadSpeed === null || speedResult.uploadSpeed < minimumSpeed) {
              const up =
                speedResult.uploadSpeed === null
                  ? null
                  : Math.round(speedResult.uploadSpeed * 100) / 100
              const details =
                speedResult.uploadSpeed === null ? 'measurement_error' : 'below_threshold'
              Sentry.logger.info(
                `[VideoExam] event=speed_test | assignmentId=${assignmentData.id} | result=fail | uploadSpeedMbps=${up} | details=${details}`
              )
              Sentry.logger.info(
                `[VideoExam] event=kick | assignmentId=${assignmentData.id} | reason=speed_test_fail | uploadSpeedMbps=${up} | details=${details}`
              )
              Sentry.captureException(new Error('Speed test failed'), {
                tags: { event: 'speed_test_kick' },
                extra: { uploadSpeedMbps: up, reason: details },
              })
              setSpeedTestState('failed')
              if (assignmentData?.id) {
                try {
                  await setAssignmentStatusSupabase(assignmentData.id, 'speed_test_fail')
                } catch (_) {
                  // Ensure we still show failed UI even if status update fails
                }
              }
              return
            }
            Sentry.logger.info(
              `[VideoExam] event=speed_test | assignmentId=${assignmentData.id} | result=pass | uploadSpeedMbps=${Math.round(speedResult.uploadSpeed * 100) / 100}`
            )
          } catch (speedError) {
            const errMsg = speedError instanceof Error ? speedError.message : String(speedError)
            Sentry.logger.info(
              `[VideoExam] event=speed_test | assignmentId=${assignmentData.id} | result=fail | details=${errMsg}`
            )
            Sentry.logger.info(
              `[VideoExam] event=kick | assignmentId=${assignmentData.id} | reason=speed_test_fail | details=${errMsg}`
            )
            Sentry.captureException(
              speedError instanceof Error ? speedError : new Error('Speed test failed'),
              {
                tags: { event: 'speed_test_kick' },
                extra: { reason: errMsg },
              }
            )
            setSpeedTestState('failed')
            if (assignmentData?.id) {
              try {
                await setAssignmentStatusSupabase(assignmentData.id, 'speed_test_fail')
              } catch (_) {
                // Ensure we still show failed UI even if status update fails
              }
            }
            return
          }
        }
      }
    } catch (error) {
      console.error('Error starting interview:', error)
      toast({
        title: 'Hata',
        description: 'Sınav başlatılırken bir hata oluştu. Lütfen tekrar deneyin.',
        variant: 'destructive',
      })
    }
  }

  const startAfterSpeedTest = () => {
    // Called when user clicks "Sınava Başla" after speed test finished
    setIsEnvironmentRecording(true)
  }

  const handleEnvironmentRecordingComplete = async () => {
    setIsEnvironmentRecording(false)
    setEnvironmentRecordingCompleted(true)

    // mark assignment in_progress
    try {
      await markInProgressSupabase(assignment.id)
    } catch (err) {
      console.error('Could not mark assignment in_progress:', err, 'assignmentId:', assignment.id)
    }

    // Start the actual interview
    startInterview()
    timeoutHandledRef.current = false

    // Restore previously answered questions after startInterview() clears the responses state
    if (preloadedAnswersRef.current.length > 0) {
      initializeResponses(preloadedAnswersRef.current)
    }

    // Reset env check count when starting
    setEnvCheckCount(0)

    // Initialize the timer
    if (!intervalRef.current) {
      intervalRef.current = window.setInterval(() => {
        setTimeLeft((prev) => {
          if (envCheckModalOpen) return prev
          if (prev <= 1) {
            clearInterval(intervalRef.current!)
            intervalRef.current = null
            if (!isCompleted && !timeoutHandledRef.current) {
              void handleTimeoutAsync()
            }
            return 0
          }
          return prev - 1
        })
      }, 1000)
    }
    toast({
      title: 'Sınav Başlatıldı',
      description: 'Ortam kaydı tamamlandı. Şimdi sınav sorularına geçiyoruz.',
    })
  }

  const handleEnvironmentVideoCapture = (blob: Blob) => {
    setEnvironmentVideoBlob(blob)
    Sentry.logger.info(
      `Environment video captured: ${blob.size} bytes for assignmentId: ${assignment.id}`
    )
    // Optionally save environment video separately
    if (assignment) {
      saveInterviewVideo(
        blob,
        `${assignment.id}`,
        assignment.test_id,
        'environment',
        'environment',
        companyId,
        { attempt_number: assignment.attempt_count || 1, candidateToken: candidateToken ?? undefined }
      )
    }
  }

  const generateQuestionsForTest = (test) => {
    // Sort choices by label (A, B, C, D, etc.) for each question
    return test.questions.map((question) => ({
      ...question,
      choices:
        question.choices?.sort((a: any, b: any) => {
          return (a.label || a.id).localeCompare(b.label || b.id)
        }) || [],
    }))
  }

  const generateEnvCheckQuestions = (totalQuestions: number): number[] => {
    // Available positions (exclude first and last question)
    const availableStart = 2
    const availableEnd = totalQuestions - 1

    // We need to select 3 positions with minimum 3-question gaps between them
    const selectedPositions: number[] = []
    const minGap = 3

    // Calculate maximum possible positions to ensure we can fit 3 questions with gaps
    // Pattern: pos1, gap(3), pos2, gap(3), pos3
    // pos1 + 3 + 1 + 3 + 1 <= availableEnd
    // Which means: pos3 = pos1 + 7, so pos1 can be at most (availableEnd - 7)

    const maxFirstPosition = availableEnd - 7 // Ensure we can fit all 3 with gaps

    if (maxFirstPosition < availableStart) {
      return [] // Cannot fit 3 questions with required gaps
    }

    // Select first position randomly within valid range
    const firstPos =
      Math.floor(Math.random() * (maxFirstPosition - availableStart + 1)) + availableStart
    selectedPositions.push(firstPos)

    // Select second position (first + gap + at least 1)
    const secondMinPos = firstPos + minGap + 1
    const secondMaxPos = Math.min(availableEnd - 4, maxFirstPosition + 3) // Ensure room for third question
    const secondPos = Math.floor(Math.random() * (secondMaxPos - secondMinPos + 1)) + secondMinPos
    selectedPositions.push(secondPos)

    // Select third position (second + gap + at least 1)
    const thirdMinPos = secondPos + minGap + 1
    const thirdMaxPos = availableEnd
    const thirdPos = Math.floor(Math.random() * (thirdMaxPos - thirdMinPos + 1)) + thirdMinPos
    selectedPositions.push(thirdPos)

    return selectedPositions
  }

  const checkForEnvModal = (responseCount: number) => {
    // Show env check modal on dynamically generated question numbers
    // Only if we haven't already shown it for this question number
    if (envCheckQuestionNumbers.includes(responseCount) && !envCheckModalOpen) {
      const checkIndex = envCheckQuestionNumbers.indexOf(responseCount)
      // Only show if this is the first time we hit this question number
      if (envCheckCount === checkIndex) {
        // Pause the timer
        setPausedTimeLeft(timeLeft)
        // Open environment check modal
        handleOpenModal()
        return true
      }
    }
    return false
  }

  const handleAnswerSubmit = async (optionId: string): Promise<boolean> => {
    if (!currentQuestion || !assignment || !candidateToken || connectionLost) return false
    if (lockedQuestionIds.has(currentQuestion.id)) return false

    const response: CandidateResponse = {
      questionId: currentQuestion.id,
      questionText: currentQuestion.text,
      choiceId: optionId,
    }

    try {
      await saveResponse({
        candidateId: assignment.employee_id,
        assignmentId: assignment.id,
        testId: assignment.test_id,
        questionId: currentQuestion.id,
        choiceId: optionId,
        companyId: companyId,
        candidateToken,
      })
      addResponse(response)
    } catch (err) {
      setConnectionLost(true)
      return false
    }

    // Check if we need to show environment check modal after adding response
    const answeredCount = responses.length
    checkForEnvModal(answeredCount)
    return true
  }

  const handleNext = async () => {
    // Check after we have the actual response count
    const answeredCount = responses.length

    if (checkForEnvModal(answeredCount)) {
      return // Don't proceed to next question if modal is shown
    }

    nextQuestion()
  }

  useEffect(() => {
    // Only run if the tab becomes hidden, interview is active, and recording started
    if (!isVisible && isInterviewActive && recordingStartTime) {
      // Always record tab switching as a cheating event
      addCheatingEvent('TAB_SWITCHING')

      // Show warning modal only on first tab switch
      if (!hasSwitchedTabRef.current) {
        setShowWarningModal(true)
        hasSwitchedTabRef.current = true
      }
    }
  }, [isVisible, isInterviewActive, recordingStartTime, addCheatingEvent])

  useEffect(() => {
    if (!assignment || isCompleted) return
    if (uploadError) {
      // Add null check for assignment
      if (assignment?.id) {
        Sentry.logger.info(
          `Setting assignment status to chunk_upload_error for assignmentId: ${assignment.id}`
        )
        setAssignmentStatusSupabase(assignment.id, 'chunk_upload_error').catch((e) =>
          console.warn(
            'Failed to set chunk upload error status:',
            e,
            'assignmentId:',
            assignment.id
          )
        )
      } else {
        console.warn('No assignment ID available for chunk upload error status update')
      }
    }
  }, [uploadError, assignment, isCompleted])

  function setAssignmentAbandonedBeacon(assignmentId: string) {
    try {
      Sentry.logger.info(`[VideoExam] event=kick | assignmentId=${assignmentId} | reason=abandoned`)
      const payload = {
        assignmentId,
        status: 'abandoned',
        updated_at: new Date().toISOString(),
      }
      const url = `${API_BASE_URL}/api/assignments/set_status`

      fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      }).catch((e) => console.warn('Failed to set abandoned status:', e))

      // Also use sendBeacon as a fallback
      const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' })
      navigator.sendBeacon(url, blob)
    } catch (e) {
      console.warn('sendBeacon abandoned failed:', e)
    }
  }

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isInterviewActive && !isCompleted && assignment) {
        e.preventDefault()
        e.returnValue = ''
        return ''
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [isInterviewActive, isCompleted, assignment])

  useEffect(() => {
    // Handle browser back button specifically
    const handlePopState = (event: PopStateEvent) => {
      if (isInterviewActive && !isCompleted) {
        // Prevent the navigation
        event.preventDefault()

        // Show confirmation dialog
        const confirmLeave = window.confirm('Sınavdan çıkmak istediğinize emin misiniz?')

        if (confirmLeave) {
          // User confirmed, allow navigation
          if (assignment?.id) {
            setAssignmentAbandonedBeacon(assignment.id)
          }

          // Stop recording and cleanup
          mediaRecorderRef.current?.stop()
          stopWebcam()
          endInterview()

          // Navigate back
          navigate(-1)
        } else {
          // User cancelled, push the current state back to prevent navigation
          window.history.pushState(null, '', window.location.pathname)
        }
      }
    }

    // Add a history entry when interview becomes active
    if (isInterviewActive && !isCompleted) {
      window.history.pushState(null, '', window.location.pathname)
      window.addEventListener('popstate', handlePopState)
    }

    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [isInterviewActive, isCompleted, assignment, navigate, endInterview])

  useEffect(() => {
    const handleUnload = () => {
      // Add null check for assignment
      if (isInterviewActive && !isCompleted && assignment?.id) {
        setAssignmentAbandonedBeacon(assignment.id)
      }
    }

    window.addEventListener('beforeunload', handleUnload)
    return () => window.removeEventListener('beforeunload', handleUnload)
  }, [isInterviewActive, isCompleted, assignment])

  const handleEnvCheckComplete = async (blob: Blob) => {
    if (!assignment) return

    try {
      // Save the environment check video
      await saveInterviewVideo(
        blob,
        assignment.id,
        assignment.test_id,
        `environment_check_${envCheckCount + 1}`,
        'environment',
        companyId,
        {
          is_random: true,
          attempt_number: assignment.attempt_count || 1,
          candidateToken: candidateToken ?? undefined,
        }
      )

      // Close the modal first
      handleCloseModal()

      // Then increment the check count
      setEnvCheckCount((prev) => prev + 1)

      // Resume the timer with the saved time
      if (pausedTimeLeft !== null) {
        // Restart the main timer interval first
        if (!intervalRef.current) {
          intervalRef.current = window.setInterval(() => {
            setTimeLeft((prev) => {
              if (envCheckModalOpen) return prev // Don't decrease time when modal is open
              if (prev <= 1) {
                clearInterval(intervalRef.current!)
                intervalRef.current = null
                if (!isCompleted) {
                  setTimeExpired(true)
                  mediaRecorderRef.current?.stop()
                  stopWebcam()
                  endInterview()
                }
                return 0
              }
              return prev - 1
            })
          }, 1000)
        }

        // Then set the correct time
        setTimeLeft(pausedTimeLeft)
        setPausedTimeLeft(null)
      }

      toast({
        title: 'Çevre Kontrolü Tamamlandı',
        description: `Sınava kaldığınız yerden devam edebilirsiniz. (${envCheckCount + 1}/3)`,
      })
    } catch (error) {
      console.error('Error saving environment check video:', error, 'assignmentId:', assignment.id)
      toast({
        title: 'Hata',
        description: 'Çevre kontrolü videosu kaydedilemedi.',
        variant: 'destructive',
      })
    }
  }

  // Update the EnvCheckModal component props
  {
    isInterviewActive && (
      <EnvCheckModal
        isOpen={envCheckModalOpen}
        onClose={() => {
          // Don't allow closing without completing
          // setEnvCheckModalOpen(false)
        }}
        onComplete={handleEnvCheckComplete}
        checkNumber={envCheckCount + 1}
      />
    )
  }

  const logError = (type: string, message: string, stack?: string) => {
    if (!assignment) return

    s3LogError(assignment.id, assignment.employee_id, type, message, stack).catch((err) =>
      console.error('Failed to log error:', err, 'assignmentId:', assignment.id)
    )
  }

  useEffect(() => {
    if (!assignment) return

    const handleWindowError = (event: ErrorEvent) => {
      s3LogError(
        assignment.id,
        assignment.employee_id,
        'javascript_error',
        event.message,
        event.error?.stack
      ).catch((err) =>
        console.error('Failed to log window error:', err, 'assignmentId:', assignment.id)
      )
    }

    const handleRejection = (event: PromiseRejectionEvent) => {
      s3LogError(
        assignment.id,
        assignment.employee_id,
        'unhandled_promise_rejection',
        String(event.reason),
        event.reason?.stack
      ).catch((err) =>
        console.error('Failed to log promise rejection:', err, 'assignmentId:', assignment.id)
      )
    }

    window.addEventListener('error', handleWindowError)
    window.addEventListener('unhandledrejection', handleRejection)

    return () => {
      window.removeEventListener('error', handleWindowError)
      window.removeEventListener('unhandledrejection', handleRejection)
    }
  }, [assignment])

  useEffect(() => {
    if (isInterviewActive && !webcamActive && !isCompleted) {
      setTimeout(() => {
        startWebcam()
      }, 500)
    }
  }, [isInterviewActive, webcamActive, isCompleted])

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      stopWebcam()
      endInterview()
    }
  }, [])

  useEffect(() => {
    if (timeLeft === 60) {
      toast({
        title: 'Son 1 dakika kaldı!',
        variant: 'default',
      })
    }
  }, [timeLeft, toast])

  useEffect(() => {
    if (!isInterviewActive || interviewKicked || isCompleted) return

    cameraCheckFailCountRef.current = 0
    micCheckFailCountRef.current = 0

    const checkMediaAccess = () => {
      // Recording stopped because the user submitted — don't kick during finalization
      if (finalizeBySubmitRef.current) return
      // Don't check while env-check modal is open (modal opens its own stream,
      // which can transiently affect track readyState on some devices)
      if (envCheckModalOpenRef.current) return

      try {
        const stream = streamRef.current
        // Stream not yet acquired (startup race) — skip rather than false-kick
        if (!stream) return

        const videoActive =
          stream.active &&
          stream.getVideoTracks().length > 0 &&
          stream.getVideoTracks()[0].readyState === 'live'
        const audioActive =
          stream.active &&
          stream.getAudioTracks().length > 0 &&
          stream.getAudioTracks()[0].readyState === 'live'

        if (!videoActive && !interviewKicked) {
          cameraCheckFailCountRef.current += 1
          Sentry.logger.info(
            `[VideoExam] event=camera_check_fail | assignmentId=${assignment.id} | count=${cameraCheckFailCountRef.current} | grace=${MEDIA_GRACE_CHECKS}`
          )
          if (cameraCheckFailCountRef.current >= MEDIA_GRACE_CHECKS) {
            setCameraError('Kamera erişimi kaybedildi veya kapatıldı.')
            setInterviewKicked(true)
            endInterview()
            stopWebcam()
            addCheatingEvent('CAMERA_ACCESS_LOST')
          }
        } else {
          cameraCheckFailCountRef.current = 0
        }

        if (!audioActive && !interviewKicked) {
          micCheckFailCountRef.current += 1
          Sentry.logger.info(
            `[VideoExam] event=mic_check_fail | assignmentId=${assignment.id} | count=${micCheckFailCountRef.current} | grace=${MEDIA_GRACE_CHECKS}`
          )
          if (micCheckFailCountRef.current >= MEDIA_GRACE_CHECKS) {
            setMicError('Mikrofon erişimi kaybedildi veya kapatıldı.')
            setInterviewKicked(true)
            endInterview()
            stopWebcam()
            addCheatingEvent('MIC_ACCESS_LOST')
          }
        } else {
          micCheckFailCountRef.current = 0
        }
      } catch (err) {
        setCameraError('Kamera/mikrofon kontrolünde bilinmeyen bir hata oluştu.')
        setInterviewKicked(true)
        endInterview()
        stopWebcam()
      }
    }

    const interval = setInterval(checkMediaAccess, 2500)

    return () => clearInterval(interval)
  }, [isInterviewActive, interviewKicked, isCompleted])

  useEffect(() => {
    if (interviewKicked && assignment?.id && !isCompleted) {
      const details = cameraError ? 'camera_lost' : micError ? 'mic_lost' : 'camera_or_mic_lost'
      Sentry.logger.info(
        `[VideoExam] event=kick | assignmentId=${assignment.id} | reason=camera_access_error | details=${details}`
      )
      Sentry.logger.info(
        `Setting assignment status to camera_access_error for assignmentId: ${assignment.id}`
      )
      Sentry.captureException(new Error('Camera/mic access lost'), {
        tags: { event: 'camera_kick' },
        extra: { details, cameraError, micError },
      })
      setAssignmentStatusSupabase(assignment.id, 'camera_access_error').catch((e) =>
        console.error('Failed to set camera_access_error:', e, 'assignmentId:', assignment.id)
      )

      if (cameraError) {
        addCheatingEvent('CAMERA_ACCESS_LOST')
      } else if (micError) {
        addCheatingEvent('MIC_ACCESS_LOST')
      }
    }
  }, [interviewKicked, assignment, isCompleted, cameraError, micError])

  useEffect(() => {
    if (!interviewKicked) {
      setCameraError(null)
      setMicError(null)
    }
  }, [interviewKicked])

  if (isMobile) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <Card className="max-w-sm w-full mx-auto shadow-md">
          <CardHeader className="bg-red-50">
            <CardTitle className="text-lg flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              UYARI
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 text-center">
            <p className="text-lg font-semibold mb-4">
              Sınavı başlatmak için lütfen bir <span className="text-primary">bilgisayardan</span>{' '}
              giriş yapınız.
            </p>
            <p className="text-gray-600">
              Mobil veya küçük ekranlı cihazlarla erişim desteklenmemektedir.
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }
  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <SidebarProvider defaultOpen={true}>
        <div className="group/sidebar-wrapper flex min-h-svh w-full">
          {speedTestState === 'failed' ? (
            // Speed Test Failure Screen
            <div className="max-w-lg mx-auto my-16 text-center w-full px-4">
              <div className="rounded-full bg-orange-100 p-4 w-20 h-20 flex items-center justify-center mx-auto mb-6">
                <AlertCircle className="h-10 w-10 text-orange-600" />
              </div>
              <h1 className="text-2xl font-bold mb-4">Düşük Bağlantı Hızı</h1>

              <p className="text-gray-600 mb-8 leading-relaxed">
                İnternet bağlantı hızınız sınav için yeterli değil. İnternet bağlantınızı kontrol
                ederek tekrar giriş yapmanızı rica ederiz.
              </p>
              <p className="text-gray-600 mb-8 leading-relaxed">
                Aynı sınav kodunuz ile tekrar giriş yapabilirsiniz.
              </p>

              <Button asChild>
                <Link to="/">
                  Ana Sayfaya Dön
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          ) : uploadError ? (
            <div className="max-w-md mx-auto my-16 text-center w-full">
              <div className="rounded-full bg-red-100 p-4 w-20 h-20 flex items-center justify-center mx-auto mb-6">
                <AlertCircle className="h-10 w-10 text-red-600" />
              </div>
              <h1 className="text-2xl font-bold mb-4">Video Yükleme Hatası</h1>
              <p className="text-gray-600 mb-8">
                İnternet bağlantınızdan kaynaklı video yüklenme hatası aldınız.
                <br />
                İnternet bağlantı hızınız yetersiz.
              </p>
              <Button asChild>
                <Link to="/">
                  Ana Sayfaya Dön
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          ) : timeExpired ? (
            <div className="max-w-md mx-auto my-16 text-center w-full">
              <div className="rounded-full bg-orange-100 p-4 w-20 h-20 flex items-center justify-center mx-auto mb-6">
                <Timer className="h-10 w-10 text-orange-600" />
              </div>
              <h1 className="text-2xl font-bold mb-4">Süre Doldu</h1>
              <p className="text-gray-600 mb-8">
                Sınav süresi sona erdi. Süre aşımı nedeniyle sınavsonlandırıldı.
              </p>
              <Button asChild>
                <Link to="/">
                  Ana Sayfaya Dön
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          ) : interviewKicked ? (
            <div className="max-w-md mx-auto my-16 text-center w-full">
              <div className="rounded-full bg-red-100 p-4 w-20 h-20 flex items-center justify-center mx-auto mb-6">
                <AlertCircle className="h-10 w-10 text-red-600" />
              </div>
              <h1 className="text-2xl font-bold mb-4">
                {micError && cameraError
                  ? 'Kamera ve Mikrofon Erişimi Kaybedildi'
                  : micError
                    ? 'Mikrofon Erişimi Kaybedildi'
                    : cameraError
                      ? 'Kamera Erişimi Kaybedildi'
                      : 'Kamera/Mikrofon Erişimi Kaybedildi'}
              </h1>
              <p className="text-gray-600 mb-8">
                {micError && cameraError
                  ? 'Sınav sırasında kamera ve mikrofon bağlantınız kesildi.'
                  : micError
                    ? 'Sınav sırasında mikrofon bağlantınız kesildi.'
                    : cameraError
                      ? 'Sınav sırasında kamera bağlantınız kesildi.'
                      : 'Kamera veya mikrofon bağlantınız kesildi.'}
                <br />
                Sınavdan çıkarıldınız. Lütfen cihaz ve tarayıcı izinlerinizi kontrol edin.
              </p>

              <Button asChild>
                <Link to="/">
                  Ana Sayfaya Dön
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          ) : (
            <>
              {isInterviewActive && !isCompleted && (
                <Sidebar>
                  <SidebarHeader>
                    <div className="flex justify-between items-center px-2 py-1">
                      {currentTestName && (
                        <span className="text-sm font-medium text-primary">{currentTestName}</span>
                      )}
                    </div>
                  </SidebarHeader>

                  <SidebarContent>
                    <SidebarGroup>
                      <SidebarGroupLabel>Kamera</SidebarGroupLabel>
                      <SidebarGroupContent>
                        <div className="flex flex-col gap-3">
                          <div className="relative w-full aspect-video bg-gray-100 rounded-md overflow-hidden">
                            {webcamActive ? (
                              <>
                                <video
                                  ref={videoRef}
                                  autoPlay
                                  playsInline
                                  muted
                                  className="w-full h-full object-cover"
                                  style={{
                                    display: 'block',
                                  }}
                                />
                                <FaceDetectionOverlay
                                  videoRef={videoRef}
                                  isActive={webcamActive}
                                  onGazeUpdate={handleGazeUpdate}
                                  onFaceDetectionUpdate={updateFaceDetection}
                                />
                                <LipSpeechDetection
                                  videoRef={videoRef}
                                  isActive={webcamActive}
                                  onCheatingEvent={(type, metadata) => {
                                    // metadata will now be: { start_sec: X, end_sec: Y, duration: Z }
                                    addCheatingEvent(type as CheatingEvent['type'], metadata)
                                  }}
                                />
                              </>
                            ) : (
                              <div className="flex items-center justify-center h-full">
                                <VideoOff className="h-8 w-8 text-gray-400" />
                                <span className="ml-2 text-sm text-gray-500">
                                  Kamera bekleniyor...
                                </span>
                              </div>
                            )}
                          </div>

                          <div className="flex flex-col gap-2 px-1 mt-1">
                            <div className="flex items-center gap-1.5 self-start">
                              <Timer className="h-4 w-4 text-gray-500" />
                              <span className="text-sm font-medium">{formatTime(timeLeft)}</span>
                            </div>
                            <SidebarGroupLabel>Soru Navigasyonu</SidebarGroupLabel>
                            <div className="grid grid-cols-4 gap-3 p-2">
                              {questions.map((_, index) => {
                                const isAnswered = responses.some(
                                  (r) => r.questionId === questions[index]?.id
                                )
                                const isCurrent = index === currentQuestionIndex
                                return (
                                  <div key={index} className="flex flex-col items-center gap-1">
                                    <button
                                      onClick={() => setCurrentQuestionIndex(index)}
                                      className={`
                                      relative w-12 h-12 rounded-md border-2 flex items-center justify-center text-base font-medium transition
                                      ${
                                        isCurrent
                                          ? 'border-gray-400 bg-gray-100 text-gray-900'
                                          : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                                      }
                                      focus:outline-none
                                    `}
                                      aria-label={`Soru ${index + 1}`}
                                    >
                                      <span>{index + 1}</span>
                                      {isAnswered && (
                                        <CheckCircle className="absolute -top-2 -right-2 h-4 w-4 text-green-600 bg-white rounded-full ring-2 ring-white" />
                                      )}
                                    </button>
                                  </div>
                                )
                              })}
                            </div>
                          </div>
                        </div>
                      </SidebarGroupContent>
                    </SidebarGroup>
                  </SidebarContent>
                </Sidebar>
              )}

              <SidebarInset>
                <main className="flex-1 container mx-auto px-4 py-8">
                  {speedTestState === 'running' && (
                    <div className="max-w-md mx-auto my-16 text-center">
                      <div className="rounded-full bg-blue-100 p-4 w-20 h-20 flex items-center justify-center mx-auto mb-6 animate-pulse">
                        <Video className="h-10 w-10 text-blue-600" />
                      </div>
                      <h1 className="text-2xl font-bold mb-4">İnternet Hız Testi</h1>
                      <p className="text-gray-600 mb-4">
                        İnternet hız testi yapılıyor. Lütfen bekleyiniz...
                      </p>
                      <div className="flex justify-center">
                        <div className="flex justify-center animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      </div>
                    </div>
                  )}

                  {speedTestState === 'completed' &&
                    !isEnvironmentRecording &&
                    !environmentRecordingCompleted &&
                    !isInterviewActive &&
                    !isCompleted && (
                      <div className="max-w-md mx-auto my-16 text-center">
                        <div className="rounded-full bg-green-100 p-4 w-20 h-20 flex items-center justify-center mx-auto mb-6">
                          <CheckCircle className="h-10 w-10 text-green-600" />
                        </div>
                        <h1 className="text-2xl font-bold mb-4">İnternet Hız Testi Tamamlandı</h1>
                        <p className="text-gray-600 mb-6">
                          Ortam kaydını başlatmak için aşağıdaki butona tıklayınız.
                        </p>
                        <div className="mt-6">
                          <div className="mx-auto w-full max-w-xs">
                            <Button onClick={startAfterSpeedTest} className="w-full">
                              <Video className="h-4 w-4 mr-2" />
                              Sınava Başla
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}

                  {/* Access Code Entry Screen */}
                  {speedTestState === 'idle' &&
                    !isEnvironmentRecording &&
                    !isInterviewActive &&
                    !isCompleted &&
                    !isFinalizing && (
                      <div className="max-w-4xl mx-auto my-12">
                        <Card className="w-full max-w-md mx-auto shadow-md">
                          <CardHeader className="bg-gray-50">
                            <CardTitle className="text-lg flex items-center gap-2">
                              <KeyRound className="h-5 w-5 text-interview" />
                              Sınav Erişimi
                            </CardTitle>
                          </CardHeader>
                          <form onSubmit={handleStartInterview}>
                            <CardContent className="p-6 space-y-6">
                              <div className="space-y-2">
                                <Label htmlFor="accessCode">Erişim Kodu</Label>
                                <Input
                                  id="accessCode"
                                  placeholder="Örnek: ABC123"
                                  value={accessCode}
                                  onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
                                  className="text-center text-lg tracking-wider uppercase"
                                  maxLength={8}
                                  required
                                />
                              </div>

                              <ConsentCheckbox
                                checked={consentChecked}
                                onCheckedChange={setConsentChecked}
                              />

                              <InformationConsentCheckbox
                                checked={informationConsentChecked}
                                onCheckedChange={setInformationConsentChecked}
                              />

                              <div className="space-y-2">
                                <h3 className="text-sm font-medium">Önemli Bilgiler</h3>
                                <ul className="text-sm text-gray-600 space-y-1 pl-5 list-disc">
                                  <li>
                                    Sınava izole bir ortamda giriş yapınız ve sınav süresi
                                    içerisinde başka sekmelere geçiş yapmayınız.
                                  </li>
                                  <li>
                                    Erişim kodları tek kullanımlıktır, sınav esnasında sayfayı
                                    yenilemeyiniz.{' '}
                                  </li>
                                </ul>
                              </div>

                              <Button
                                type="submit"
                                className="w-full mt-4"
                                disabled={!consentChecked || !informationConsentChecked}
                              >
                                <Video className="h-4 w-4 mr-2" />
                                Sınava Başla
                              </Button>
                            </CardContent>
                          </form>
                        </Card>
                      </div>
                    )}

                  {/* Environment Recording Screen */}
                  {isEnvironmentRecording && !environmentRecordingCompleted && !isFinalizing && (
                    <EnvironmentRecording
                      onComplete={handleEnvironmentRecordingComplete}
                      onVideoCapture={handleEnvironmentVideoCapture}
                      isActive={isEnvironmentRecording}
                    />
                  )}

                  {/* Interview Questions Screen */}
                  {isInterviewActive &&
                    currentQuestion &&
                    !isCompleted &&
                    environmentRecordingCompleted &&
                    !isFinalizing && (
                      <div className="max-w-4xl mx-auto my-12">
                        <h1 className="text-2xl font-bold mb-2">
                          {currentTestName ? currentTestName : 'Videolu Mülakat'}: {candidateName}
                        </h1>

                        {connectionLost && (
                          <div
                            className="flex items-center gap-2 mb-4 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-amber-900"
                            role="status"
                            aria-live="polite"
                          >
                            <WifiOff className="h-5 w-5 shrink-0" />
                            <span className="text-sm">
                              Bağlantın koptu, yeniden bağlanılıyor. Bağlantı dönene kadar verdiğin
                              cevaplar kaydedilmiyor.
                            </span>
                          </div>
                        )}

                        <div className="flex items-center justify-between mb-6">
                          <div className="flex items-center gap-2">
                            <Timer className="h-5 w-5 text-gray-500" />
                            <span className="text-lg font-medium">{formatTime(timeLeft)}</span>
                          </div>
                        </div>

                        <TextQuestionCard
                          question={currentQuestion}
                          currentIndex={currentQuestionIndex}
                          totalQuestions={questions.length}
                          onAnswerSubmit={handleAnswerSubmit}
                          selectedOption={currentResponse?.choiceId || ''}
                          onPrevious={previousQuestion}
                          onNext={handleNext}
                          onSubmit={handleSubmit}
                          hasResponse={hasResponse}
                          isLast={isLastQuestion}
                          isSubmitting={isSubmitting}
                          isLocked={lockedQuestionIds.has(currentQuestion.id)}
                          isDisabled={connectionLost}
                        />
                      </div>
                    )}

                  {/* Completion Screen */}
                  {isCompleted && !isFinalizing && (
                    <div className="max-w-md mx-auto my-16 text-center">
                      <div className="rounded-full bg-green-100 p-4 w-20 h-20 flex items-center justify-center mx-auto mb-6">
                        <CheckCircle className="h-10 w-10 text-green-600" />
                      </div>
                      <h1 className="text-2xl font-bold mb-4">Sınav Tamamlandı</h1>
                      <p className="text-gray-600 mb-8">
                        Teşekkürler {candidateName}, sınavınız başarıyla tamamlandı.
                      </p>
                      <div className="mb-8 max-w-xs mx-auto">
                        <Card>
                          <CardContent className="p-4">
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span>Sınav:</span>
                                <span className="font-medium">{currentTestName}</span>
                              </div>
                              <div className="flex justify-between">
                                <span>Toplam Süre:</span>
                                <span className="font-medium">
                                  {formatTime(timeLimitSeconds - timeLeft)}
                                </span>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                      <Button asChild>
                        <Link to="/">
                          Ana Sayfaya Dön
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Link>
                      </Button>
                    </div>
                  )}

                  {isFinalizing && (
                    <div
                      className="fixed inset-0 z-[200] flex flex-col items-center justify-center bg-white"
                      role="alert"
                      aria-live="polite"
                      aria-busy="true"
                    >
                      <div className="rounded-full bg-indigo-100 p-4 w-20 h-20 flex items-center justify-center mx-auto mb-6">
                        <Loader2 className="h-10 w-10 animate-spin text-indigo-600" />
                      </div>

                      <h1 className="text-2xl font-bold mb-4 text-gray-900">Lütfen Bekleyin</h1>

                      <p className="text-gray-700 mb-2 max-w-sm text-center">
                        Yanıtlarınız ve videonuz kaydediliyor, lütfen sayfayı kapatmayınız.
                      </p>
                      <p className="text-gray-500 text-sm max-w-xs text-center">
                        Bu işlem internet hızınıza bağlı olarak birkaç dakika sürebilir.
                      </p>
                    </div>
                  )}
                </main>
              </SidebarInset>
            </>
          )}
        </div>
      </SidebarProvider>

      <Footer />
      {/* Warning Modal for First Tab Switch */}
      {showWarningModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full border-l-4 border-yellow-500 animate-in fade-in zoom-in duration-200">
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-3">
                <div className="bg-yellow-100 p-2 rounded-full">
                  <AlertCircle className="h-6 w-6 text-yellow-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">Ekran Değişikliği Tespit Edildi</h3>
              </div>

              <p className="text-gray-600">
                Sınav esnasında ekran değiştirdiğiniz veya başka bir sekmeye geçtiğiniz tespit
                edilmiştir.
                <br />
              </p>
              <div className="bg-red-50 p-3 rounded border border-red-100 flex gap-2">
                <span className="text-red-700 text-sm font-medium leading-snug">
                  Bir kez daha sekme/ ekran değişikligi yapmanız durumunda sınavınız geçersiz
                  sayılacak ve sonucunuz "0" olarak sisteme işlenecektir.
                </span>
              </div>

              <div className="flex justify-end mt-2">
                <Button
                  onClick={() => setShowWarningModal(false)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6"
                >
                  Anladım, Onaylıyorum
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isInterviewActive && (
        <EnvCheckModal
          isOpen={envCheckModalOpen}
          onClose={() => {
            // Don't allow closing without completing
            // setEnvCheckModalOpen(false)
          }}
          onComplete={handleEnvCheckComplete}
          checkNumber={envCheckCount + 1} // Pass the next check number (1, 2, 3)
        />
      )}
    </div>
  )
}
export default VideoInterview
